import React, { useState, useEffect } from "react";
import { Marker, Source, Layer } from "react-map-gl";
import * as turf from "@turf/turf";
import PropTypes from "prop-types";
import Pin from "./pin";

export const DynamicPoint = ({ mapRef, points, realTimePoint, setPopupInfo, showRoute }) => {

    const [visitedPoints, setVisitedPoints] = useState([]);
    const [routeLine, setRouteLine] = useState(null);

    const pointLayer = {
        id: "dynamic-point",
        type: "symbol",
        layout: {
            "icon-image": "custom-marker",
            "icon-size": 1.5,
        },
    };

    const routeLayer = {
        id: "route-layer",
        type: "line",
        paint: {
            "line-color": "#ff0000",
            "line-width": 4,
        },
    };

    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map) return;

        map.on('styledata', () => {
            if (!map.hasImage("custom-marker")) {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.src = "https://upload.wikimedia.org/wikipedia/commons/e/ec/RedDot.svg";
                img.onload = () => {
                    if (!map.hasImage("custom-marker")) {
                        map.addImage("custom-marker", img);
                    }
                };
            }
        });

        return () => {
            map.off('styledata');
        };
    }, [mapRef]);

    useEffect(() => {
        if (!realTimePoint) return;

        const currentPoint = turf.point(realTimePoint);
        const nextVisitedPoints = points.filter((p) => {
            const distanceToPoint = turf.distance(
                currentPoint,
                turf.point([p.longitude, p.latitude]),
                { units: "kilometers" }
            );
            return distanceToPoint < 0.01 || visitedPoints.some((v) => v.id === p.id);
        });

        if (JSON.stringify(nextVisitedPoints) !== JSON.stringify(visitedPoints)) {
            setVisitedPoints(nextVisitedPoints);
        }

        const routeCoordinates = nextVisitedPoints.map((p) => [p.longitude, p.latitude]);
        routeCoordinates.push(realTimePoint);
        if (routeCoordinates.length >= 2) {
            const line = turf.lineString(routeCoordinates);
            setRouteLine(line);
        } else {
            setRouteLine(null);
        }
    }, [realTimePoint, points, visitedPoints]);

    return (
        showRoute && (
            <>
                {points.map((point) => (
                    <Marker
                        key={`point-${point.id}`}
                        longitude={point.longitude}
                        latitude={point.latitude}
                        anchor="bottom"
                        onClick={e => {
                            e.originalEvent.stopPropagation();
                            setPopupInfo(point);
                        }}
                        data-testid={`marker-${point.id}`}
                    >
                        <Pin size={"30px"} />
                    </Marker>
                ))}

                {routeLine && (
                    <Source
                        type="geojson"
                        data={{
                            type: "Feature",
                            geometry: {
                                type: "LineString",
                                coordinates: routeLine.geometry.coordinates,
                            },
                        }}
                    >
                        <Layer {...routeLayer} />
                    </Source>
                )}

                {realTimePoint && (
                    <Source
                        type="geojson"
                        data={{
                            type: "Feature",
                            geometry: {
                                type: "Point",
                                coordinates: realTimePoint,
                            },
                        }}
                    >
                        <Layer {...pointLayer} />
                    </Source>
                )}
            </>
        ));
};

DynamicPoint.propTypes = {
    mapRef: PropTypes.object,
    points: PropTypes.array,
    realTimePoint: PropTypes.array,
    setPopupInfo: PropTypes.func,
    showRoute: PropTypes.bool,
}